import { Badge, ActionIcon } from 'rizzui';
import MessagesDropdown from '@/layouts/messages-dropdown';
import ProfileMenu from '@/layouts/profile-menu';
import RingBellSolidIcon from '@core/components/icons/ring-bell-solid';
import ChatSolidIcon from '@core/components/icons/chat-solid';
import NotificationDropdown from './notification-dropdown';
import { useNotifications } from '@/providers/notifications';
import { messagesData } from '@/data/messages';
import { DEMO_DATA_ENABLED } from '@/demo';
import { useSession } from '@core/providers/session';

/**
 * Deliberate exception to the "copy verbatim" rule, in the same spirit as `data/notifications.ts`
 * and `data/messages.ts`'s own header comment: those two already route through `demoOnly()` so the
 * dropdown lists are empty outside visual-QA mode rather than showing fabricated rows. This file's
 * own "unread" dots were still hard-coded `true` regardless — an empty dropdown behind a badge that
 * always claims something's new. Deriving the dot from the same (already-correct) data closes that
 * gap instead of leaving it as the one piece of copied chrome that didn't get the `018` fix.
 */
const hasUnreadMessages = messagesData.some((item) => item.unRead);

// Non-mechanical edit, same reason: outside visual-QA mode there are no notifications or messages
// to list, and both dropdowns' "View all" links lead to template routes this app does not have.

export default function HeaderMenuRight() {
  // The bell's dot is the host inbox's own unread count (providers/notifications.tsx), not a demo array.
  const hasUnreadNotifications = useNotifications().unreadCount > 0;
  const { user } = useSession();
  return (
    <div className="ms-auto flex shrink-0 items-center gap-3">
      {/* The Marketplace admin portal's "email · role" pill (2026-09-28, non-mechanical edit). */}
      {user ? (
        <span className="hidden rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-600 md:inline">
          {user.email ?? user.name}
          {user.role ? <span className="capitalize"> · {user.role.replace(/_/g, ' ')}</span> : null}
        </span>
      ) : null}
    <div className={`grid shrink-0 ${DEMO_DATA_ENABLED ? 'grid-cols-3' : 'grid-cols-2'} items-center gap-2 text-gray-700 xs:gap-3 xl:gap-4`}>
      <NotificationDropdown>
        <ActionIcon
          aria-label="Notification"
          variant="text"
          className="relative h-[34px] w-[34px] shadow backdrop-blur-md dark:bg-gray-100 md:h-9 md:w-9"
        >
          <RingBellSolidIcon className="h-[18px] w-auto" />
          {hasUnreadNotifications ? (
            <Badge
              renderAsDot
              color="warning"
              enableOutlineRing
              className="absolute right-2.5 top-2.5 -translate-y-1/3 translate-x-1/2"
            />
          ) : null}
        </ActionIcon>
      </NotificationDropdown>
      {DEMO_DATA_ENABLED ? (
      <MessagesDropdown>
        <ActionIcon
          aria-label="Messages"
          variant="text"
          className="relative h-[34px] w-[34px] shadow backdrop-blur-md dark:bg-gray-100 md:h-9 md:w-9"
        >
          <ChatSolidIcon className="h-[18px] w-auto" />
          {hasUnreadMessages ? (
            <Badge
              renderAsDot
              color="success"
              enableOutlineRing
              className="absolute right-2.5 top-2.5 -translate-y-1/3 translate-x-1/2"
            />
          ) : null}
        </ActionIcon>
      </MessagesDropdown>
      ) : null}

      <ProfileMenu />
    </div>
    </div>
  );
}
